import React from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {Link} from 'react-router-dom';

import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useCurrentOrgIdPrefix} from 'src/hooks/useCurrentOrgIdPrefix';
import {
  SetupWizardOrganization,
  SetupWizardProject,
  makeSetupProjectsQueryKey,
  setupWizardApi,
} from 'src/lib/api/setupWizard';

type InlineAction =
  | {type: 'rename'; projectId: string; name: string}
  | {type: 'duplicate'; projectId: string; name: string};

export default function SetupWizardSelectPage() {
  const currentOrgIdPrefix = useCurrentOrgIdPrefix();
  const queryClient = useQueryClient();
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);

  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [inlineAction, setInlineAction] = React.useState<InlineAction | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);

  const {data: organizations, isLoading: orgsLoading} = useQuery(
    ['setupWizard', 'organizations'],
    () => setupWizardApi.listOrganizations(),
    {staleTime: 60_000}
  );

  const {data: projects, isLoading: projectsLoading} = useQuery(
    makeSetupProjectsQueryKey(selectedOrgId),
    () => setupWizardApi.listProjects(selectedOrgId!),
    {enabled: !!selectedOrgId, staleTime: 30_000}
  );

  React.useEffect(() => {
    if (organizations && organizations.length > 0 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  // Close kebab menu on any outside click
  React.useEffect(() => {
    if (openMenuId === null) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const invalidateProjects = () =>
    queryClient.invalidateQueries(makeSetupProjectsQueryKey(selectedOrgId));

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId || !newProjectName.trim()) return;
    setCreateError(null);
    setIsCreating(true);
    try {
      const payload = await setupWizardApi.createProject(selectedOrgId, newProjectName.trim());
      const projectId = payload.data.project ?? '';
      window.location.href = `/${currentOrgIdPrefix}/setup/${selectedOrgId}/${projectId}`;
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const startAction = (type: 'rename' | 'duplicate', project: SetupWizardProject) => {
    setActionError(null);
    setInlineAction({
      type,
      projectId: project.id,
      name: type === 'rename' ? project.displayName : `${project.displayName} (copy)`,
    });
  };

  const cancelAction = () => {
    setInlineAction(null);
    setActionError(null);
  };

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineAction || inlineAction.type !== 'rename' || !selectedOrgId) return;
    const name = inlineAction.name.trim();
    if (!name) return;
    setActionError(null);
    setActionLoading(true);
    try {
      await setupWizardApi.renameProject(selectedOrgId, inlineAction.projectId, name);
      setInlineAction(null);
      await invalidateProjects();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to rename project');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inlineAction || inlineAction.type !== 'duplicate' || !selectedOrgId) return;
    const name = inlineAction.name.trim();
    if (!name) return;
    setActionError(null);
    setActionLoading(true);
    try {
      const payload = await setupWizardApi.duplicateProject(
        selectedOrgId,
        inlineAction.projectId,
        name
      );
      const newProjectId = payload.data.project ?? '';
      window.location.href = `/${currentOrgIdPrefix}/setup/${selectedOrgId}/${newProjectId}`;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to duplicate project');
      setActionLoading(false);
    }
  };

  let content: React.ReactNode;
  if (orgsLoading || !organizations) {
    content = <LoadingSpinner />;
  } else {
    content = (
      <div className="space-y-6">
        <div>
          <label
            htmlFor="setup-org-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Organization
          </label>
          <select
            id="setup-org-select"
            className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            value={selectedOrgId ?? ''}
            onChange={(e) => {
              setSelectedOrgId(e.target.value || null);
              setInlineAction(null);
              setActionError(null);
            }}
          >
            {organizations.map((org: SetupWizardOrganization) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        {selectedOrgId && (
          <>
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Projects</h2>
              {projectsLoading ? (
                <LoadingSpinner />
              ) : projects && projects.length > 0 ? (
                <ul className="space-y-1">
                  {projects.map((project) => {
                    const isRenaming =
                      inlineAction?.type === 'rename' &&
                      inlineAction.projectId === project.id;
                    const isDuplicating =
                      inlineAction?.type === 'duplicate' &&
                      inlineAction.projectId === project.id;

                    return (
                      <li key={project.id}>
                        <div className="flex items-center gap-1">
                          {isRenaming ? (
                            <form
                              onSubmit={handleRename}
                              className="flex items-center gap-2 flex-1"
                            >
                              <input
                                autoFocus
                                type="text"
                                className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                                value={inlineAction.name}
                                onChange={(e) =>
                                  setInlineAction({...inlineAction, name: e.target.value})
                                }
                                disabled={actionLoading}
                              />
                              <button
                                type="submit"
                                disabled={actionLoading || !inlineAction.name.trim()}
                                className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                              >
                                {actionLoading ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={cancelAction}
                                disabled={actionLoading}
                                className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <Link
                              to={`/${currentOrgIdPrefix}/setup/${selectedOrgId}/${project.id}`}
                              className="text-blue-600 hover:text-blue-500 text-sm"
                            >
                              {project.displayName}
                            </Link>
                          )}

                          {!isRenaming && (
                            <div className="relative ml-1">
                              <button
                                aria-label="Project actions"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(
                                    openMenuId === project.id ? null : project.id
                                  );
                                }}
                                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-sm leading-none"
                              >
                                ⋮
                              </button>
                              {openMenuId === project.id && (
                                <div className="absolute left-0 top-full mt-1 z-10 w-32 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 py-1">
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      startAction('rename', project);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    Rename
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      startAction('duplicate', project);
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    Duplicate
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {isDuplicating && (
                          <form
                            onSubmit={handleDuplicate}
                            className="mt-1 ml-0 flex flex-wrap items-center gap-2"
                          >
                            <input
                              autoFocus
                              type="text"
                              className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
                              value={inlineAction.name}
                              onChange={(e) =>
                                setInlineAction({...inlineAction, name: e.target.value})
                              }
                              placeholder="New project name"
                              disabled={actionLoading}
                            />
                            <button
                              type="submit"
                              disabled={actionLoading || !inlineAction.name.trim()}
                              className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                            >
                              {actionLoading ? 'Duplicating…' : 'Duplicate'}
                            </button>
                            <button
                              type="button"
                              onClick={cancelAction}
                              disabled={actionLoading}
                              className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </form>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-gray-500">No projects yet. Create one below.</p>
              )}
              {actionError && <p className="mt-2 text-red-600 text-sm">{actionError}</p>}
            </div>

            <form onSubmit={handleCreateProject} className="flex flex-wrap items-end gap-2">
              <div>
                <label
                  htmlFor="new-project-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  New project name
                </label>
                <input
                  id="new-project-name"
                  type="text"
                  className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="My Project"
                  disabled={isCreating}
                />
              </div>
              <button
                type="submit"
                disabled={isCreating || !newProjectName.trim()}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {isCreating ? 'Creating…' : 'Create project'}
              </button>
              {createError && <p className="text-red-600 text-sm">{createError}</p>}
            </form>
          </>
        )}
      </div>
    );
  }

  return (
    <PageContainer>
      <PageTitle pageStack={[{title: 'Setup wizard', to: `/${currentOrgIdPrefix}/setup`}]} />
      {content}
    </PageContainer>
  );
}
